import {
	Component,
	ElementRef,
	effect,
	input,
	linkedSignal,
	output,
	signal,
	untracked,
	viewChild,
} from '@angular/core'
import type { Result } from '@zxing/library'
import type Cropper from 'cropperjs'
import { qrOuterOverlayFromZXingResult } from './qr-util.js'
import { toArrayBuffer, toBase64 } from '../../bridges/frontend-data-bridge.js'
import type { ImageSelectorMode, Types } from '@lg/shared'

@Component({
	selector: 'lowgile-image-selector',
	templateUrl: './image-selector.component.html',
	styleUrls: ['./image-selector.component.scss'],
	standalone: false,
})
export class ImageSelectorComponent {
	private videoEl = viewChild<ElementRef<HTMLVideoElement>>('video')
	private imageEl = viewChild<ElementRef<HTMLImageElement>>('image')
	private lastBarcodeResult: Result | undefined = undefined
	private cropper?: Cropper

	allowFileSelection = input(true)
	allowedFileMimeTypes = input<string[] | undefined>(undefined)
	allowCamera = input(true)
	autoStartCapturing = input(false)
	autoUseImage = input(false)
	mode = input<ImageSelectorMode>('image')
	takePictureOnQrDetection = input(true)

	captureImageFormat = input<'png' | 'jpg'>('jpg')
	requestedImageWidth = input<number | undefined>(undefined)
	requestedImageHeight = input<number | undefined>(undefined)

	imageSrc = signal<string | undefined>(undefined)
	imagePreviewSrc = linkedSignal(this.imageSrc)
	pauseQrDetection = false

	qrCodeUsed = output<string>()
	imageUsed = output<Types.ImageSelectorOutput>()
	documentUsed = output<Types.ImageSelectorOutput>()

	captureStream = signal<MediaStream | undefined>(undefined)
	forceImageHeight = signal('')
	forceImageWidth = signal('auto')
	protected barcodeDecoder?: { stop: () => void }

	constructor() {
		effect(() => {
			if (this.autoStartCapturing()) {
				untracked(() => this.startCapturing())
			}
		})
		effect(async () => {
			if (this.mode() === 'qrCode' && this.captureStream()) {
				this.barcodeDecoder?.stop()

				const { BrowserMultiFormatReader } = await import('@zxing/library')
				const barcodeReader = new BrowserMultiFormatReader()

				await barcodeReader.decodeFromVideoElementContinuously(
					this.videoEl()?.nativeElement,
					async (result, error) => {
						if (this.pauseQrDetection) return

						if (result) {
							this.pauseQrDetection = true
							this.lastBarcodeResult = result
							// console.log('barcode detected', result)

							if (this.takePictureOnQrDetection()) {
								await this.takePictureInternal()
							}
						}
					},
				)

				this.barcodeDecoder = {
					stop: () => {
						barcodeReader.stopContinuousDecode()
						this.barcodeDecoder = undefined
					},
				}
			}
		})
	}

	async startCapturing() {
		this.pauseQrDetection = false
		this.resetCropper()
		if (!this.captureStream()) {
			this.captureStream.set(
				await navigator.mediaDevices.getUserMedia({
					video: {
						facingMode: 'environment',
						width: this.requestedImageWidth(),
						height: this.requestedImageHeight(),
					},
					audio: false,
				}),
			)
			const videoEl = this.videoEl()?.nativeElement
			videoEl.srcObject = this.captureStream()
			if (this.mode() != 'qrCode') {
				videoEl.play() // QR scanner doesn't work with autoplay
			}
		}

		this.imageSrc.set(undefined)
	}

	stopCapturing() {
		this.captureStream()
			?.getTracks()
			.forEach((track) => track.stop())
		this.captureStream.set(undefined)
		this.imageSrc.set(undefined)
		const videoEl = this.videoEl()?.nativeElement
		videoEl.srcObject = null
		this.resetCropper()
	}

	resetCropper() {
		this?.cropper?.destroy()
		this.cropper = undefined
		this.forceImageHeight.set('')
		this.forceImageWidth.set('')
	}

	cancelCapture() {
		this.stopCapturing()
		this.imageSrc.set(undefined)
	}

	private async takePictureInternal() {
		const video = this.videoEl().nativeElement
		const canvas = document.createElement('canvas')
		canvas.width = video.videoWidth
		canvas.height = video.videoHeight
		const context = canvas.getContext('2d')!
		context.drawImage(video, 0, 0, canvas.width, canvas.height)

		switch (this.mode()) {
			case 'qrCode': {
				const barcodePoints = this.lastBarcodeResult?.getResultPoints()
				if (barcodePoints?.length) {
					this.imageSrc.set(
						canvas.toDataURL(`image/${this.captureImageFormat()}`),
					) // const growAmount = (barcodePoints as any[]).reduce((sum, point) => sum + 3.5 * point.estimatedModuleSize ?? 0, 0) / barcodePoints.length

					const grownBarcodePoints = barcodePoints.map((point, idx) => {
						const next = barcodePoints[(idx + 1) % barcodePoints.length]
						const prev =
							barcodePoints[
								(idx - 1 + barcodePoints.length) % barcodePoints.length
							]

						const lenToNext = Math.sqrt(
							(next.getX() - point.getX()) ** 2 +
								(next.getY() - point.getY()) ** 2,
						)
						const lenToPrev = Math.sqrt(
							(prev.getX() - point.getX()) ** 2 +
								(prev.getY() - point.getY()) ** 2,
						)

						return {
							x:
								point.getX() -
								((point as any).estimatedModuleSize ?? 0) *
									3.5 *
									((next.getX() - point.getX()) / lenToNext +
										(prev.getX() - point.getX()) / lenToPrev),
							y:
								point.getY() -
								((point as any).estimatedModuleSize ?? 0) *
									3.5 *
									((next.getY() - point.getY()) / lenToNext +
										(prev.getY() - point.getY()) / lenToPrev),
						}
					})

					const qrOverlay = qrOuterOverlayFromZXingResult(
						this.lastBarcodeResult!,
					)

					context.beginPath()
					qrOverlay.forEach((point) => {
						context.lineTo(point.x, point.y)
					})
					context.closePath()
					context.fillStyle = 'rgba(0, 255, 0, 0.3)'
					context.fill()
					context.strokeStyle = 'rgba(0, 255, 0, 0.7)'
					context.lineWidth = 2
					context.stroke()
					this.imagePreviewSrc.set(
						canvas.toDataURL(`image/${this.captureImageFormat()}`),
					)
				}

				if (this.autoUseImage()) {
					this.useImage()
				}
				break
			}

			case 'image': {
				this.forceImageHeight.set(
					this.videoEl()?.nativeElement.clientHeight + 'px',
				)
				this.forceImageWidth.set(
					this.videoEl()?.nativeElement.clientWidth + 'px',
				)
				this.imageSrc.set(
					canvas.toDataURL(`image/${this.captureImageFormat()}`),
				)

				if (this.autoUseImage()) {
					this.useImage()
				}
				break
			}

			case 'document': {
				this.forceImageHeight.set(
					this.videoEl()?.nativeElement.clientHeight + 'px',
				)
				this.forceImageWidth.set(
					this.videoEl()?.nativeElement.clientWidth + 'px',
				)
				this.imageSrc.set(
					canvas.toDataURL(`image/${this.captureImageFormat()}`),
				)
				this.cropper?.destroy()
				const Cropper = (await import('cropperjs')).default
				this.cropper = new Cropper(this.imageEl()?.nativeElement)
				if (this.autoUseImage()) {
					console.warn(
						'Auto use picture is enabled, but not supported for document mode, ignoring',
					)
				}
				break
			}
		}
	}

	async takePicture() {
		this.lastBarcodeResult = undefined
		await this.takePictureInternal()
	}

	async useImage() {
		const imageData = this.imageSrc()
		this.imageUsed.emit(
			this.toOutputMode(imageData, `image.${this.captureImageFormat()}`),
		)
		if (this.mode() == 'qrCode') {
			this.qrCodeUsed.emit(this.lastBarcodeResult?.getText() ?? '')
		}
		if (this.mode() == 'document') {
			const cropperSelection = this.cropper?.getCropperSelection()
			const selectionCanvas = await cropperSelection.$toCanvas()

			const selectionData = selectionCanvas.toDataURL(
				`image/${this.captureImageFormat()}`,
			)
			this.documentUsed.emit(
				this.toOutputMode(
					selectionData,
					`document.${this.captureImageFormat()}`,
				),
			)
		}

		this.stopCapturing()
	}

	private toOutputMode(dataUrl: string, fileName: string) {
		return {
			dataUrl,
			get base64() {
				return this.dataUrl.split(',')[1]
			},
			get mimeType() {
				return this.dataUrl.split(',')[0].split(':')[1].split(';')[0]
			},
			get arrayBuffer() {
				return toArrayBuffer(this.base64)
			},
			fileName,
			fileNameExtension: fileName.replace(/^.*\./, ''),
		}
	}

	async onFileSelected(event: Event) {
		this.stopCapturing()
		const fileEl = event.target as HTMLInputElement
		const file = fileEl.files?.[0]
		if (file) {
			const arrayBuffer = await file.arrayBuffer()
			const base64 = toBase64(arrayBuffer)
			const dataUrl = `data:${file.type};base64,${base64}`
			const output = this.toOutputMode(dataUrl, file.name)
			this.imageUsed.emit(output)
			if (this.mode() == 'document') {
				this.documentUsed.emit(output)
			}
		}
	}
}
